import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar as CalendarIcon, Users, Phone, Mail, Mountain, Check, Star, 
  ChevronLeft, ChevronRight, X, Image as ImageIcon, Play, 
  MapPin, Train, Plane, Car, Utensils, Wifi, Eye, ZoomIn,
  Facebook, Instagram, ArrowRight, Menu, Home, Bed, Bath,
  Flame, Droplets, Sparkles, Thermometer, ChefHat
} from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';

const PublicBooking = () => {
  const [rooms, setRooms] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Booking form state
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [checkInDate, setCheckInDate] = useState(null);
  const [checkOutDate, setCheckOutDate] = useState(null);
  const [formData, setFormData] = useState({
    guest_name: '',
    guest_email: '',
    guest_phone: '',
    check_in: '',
    check_out: '',
    num_guests: 2,
    meal_plan: 'room_only',
    special_requests: '',
  });
  const [priceData, setPriceData] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [booking, setBooking] = useState(false);

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [lightboxType, setLightboxType] = useState('photo');

  // Review submission state
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    guest_name: '',
    rating: 5,
    review_text: '',
  });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [expandedReviews, setExpandedReviews] = useState({});
  
  // Meal pricing state
  const [mealPricing, setMealPricing] = useState({
    meal_price_2_persons: 1600,
    meal_price_3_persons: 1500,
    meal_price_4_plus: 1400,
    min_occupancy: 2
  });

  // Central booking section state
  const [bookingDates, setBookingDates] = useState({ checkIn: '', checkOut: '' });
  const [availableRoomsForBooking, setAvailableRoomsForBooking] = useState([]);
  const [loadingAvailableRooms, setLoadingAvailableRooms] = useState(false);
  const [centralGuestCount, setCentralGuestCount] = useState(2); // Default 2 guests (min for meals)

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Placeholder for missing images
  const PLACEHOLDER_IMAGE = 'https://placehold.co/400x300/1a1a1a/666666?text=Image+Not+Available';

  // Extract Google Drive file ID from various URL formats
  const extractGoogleDriveFileId = (url) => {
    if (!url) return null;
    
    let fileId = null;
    
    // Format: https://drive.google.com/uc?id=FILE_ID or ?export=view&id=FILE_ID
    if (url.includes('drive.google.com/uc')) {
      const idMatch = url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
      if (idMatch) fileId = idMatch[1];
    }
    // Format: https://drive.google.com/thumbnail?id=FILE_ID
    if (url.includes('drive.google.com/thumbnail')) {
      const idMatch = url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
      if (idMatch) fileId = idMatch[1];
    }
    // Format: https://drive.google.com/file/d/FILE_ID/view
    const fileIdMatch = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
    if (fileIdMatch) fileId = fileIdMatch[1];
    
    // Format: https://drive.google.com/open?id=FILE_ID
    const openIdMatch = url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
    if (!fileId && openIdMatch) fileId = openIdMatch[1];
    
    return fileId;
  };

  // Convert Google Drive share link to direct image URL
  const convertGoogleDriveImageUrl = (url) => {
    if (!url) return url;
    
    const fileId = extractGoogleDriveFileId(url);
    
    if (fileId) {
      // Use thumbnail API for images - more reliable
      return `https://drive.google.com/thumbnail?id=${fileId}&sz=w1000`;
    }
    
    return url;
  };

  // Convert Google Drive share link to embeddable video URL
  const convertGoogleDriveVideoUrl = (url) => {
    if (!url) return url;
    
    const fileId = extractGoogleDriveFileId(url);
    
    if (fileId) {
      // Use Google Drive's preview/embed URL for video playback
      return `https://drive.google.com/file/d/${fileId}/preview`;
    }
    
    return url;
  };

  // Check if URL is a Google Drive video (needs iframe)
  const isGoogleDriveVideo = (url) => {
    return url && url.includes('drive.google.com');
  };

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return PLACEHOLDER_IMAGE;
    
    // Handle Google Drive URLs for images
    if (imageUrl && imageUrl.includes('drive.google.com')) {
      return convertGoogleDriveImageUrl(imageUrl);
    }
    
    if (imageUrl.startsWith('/uploads/')) {
      return `${BACKEND_URL}/api${imageUrl}`;
    }
    if (imageUrl.startsWith('http')) {
      return imageUrl;
    }
    return `${BACKEND_URL}/api${imageUrl}`;
  };

  // Separate function for video URLs
  const getVideoUrl = (videoUrl) => {
    if (!videoUrl) return '';
    
    // Handle Google Drive URLs for videos
    if (videoUrl && videoUrl.includes('drive.google.com')) {
      return convertGoogleDriveVideoUrl(videoUrl);
    }
    
    if (videoUrl.startsWith('/uploads/')) {
      return `${BACKEND_URL}/api${videoUrl}`;
    }
    if (videoUrl.startsWith('http')) {
      return videoUrl;
    }
    return `${BACKEND_URL}/api${videoUrl}`;
  };

  // Handle image load errors
  const handleImageError = (e) => {
    e.target.onerror = null; // Prevent infinite loop
    e.target.src = PLACEHOLDER_IMAGE;
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    if (selectedRoom && formData.check_in && formData.check_out && formData.num_guests) {
      calculatePrice();
    }
  }, [selectedRoom, formData.check_in, formData.check_out, formData.num_guests, formData.meal_plan]);

  // Fetch available rooms when booking dates change
  useEffect(() => {
    const fetchAvailableRooms = async () => {
      if (!bookingDates.checkIn || !bookingDates.checkOut) {
        setAvailableRoomsForBooking([]);
        return;
      }
      
      setLoadingAvailableRooms(true);
      try {
        const response = await axios.get(`${API}/public/available-rooms?check_in=${bookingDates.checkIn}&check_out=${bookingDates.checkOut}`);
        if (response.data.success) {
          setAvailableRoomsForBooking(response.data.available_rooms);
        }
      } catch (error) {
        console.error('Error fetching available rooms:', error);
        setAvailableRoomsForBooking([]);
      } finally {
        setLoadingAvailableRooms(false);
      }
    };
    
    fetchAvailableRooms();
  }, [bookingDates.checkIn, bookingDates.checkOut]);

  const fetchAllData = async () => {
    try {
      const [roomsRes, photosRes, videosRes, reviewsRes, mealPricingRes] = await Promise.all([
        axios.get(`${API}/public/rooms`),
        axios.get(`${API}/media/list?media_type=photo`),
        axios.get(`${API}/media/list?media_type=video`),
        axios.get(`${API}/reviews`),
        axios.get(`${API}/public/meal-pricing`)
      ]);

      if (roomsRes.data.success) setRooms(roomsRes.data.rooms);
      if (photosRes.data.success) setPhotos(photosRes.data.media);
      if (videosRes.data.success) setVideos(videosRes.data.media);
      if (reviewsRes.data.success) setReviews(reviewsRes.data.reviews);
      if (mealPricingRes.data) setMealPricing(mealPricingRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculatePrice = async () => {
    setCalculating(true);
    try {
      const response = await axios.post(`${API}/public/calculate-price`, {
        room_id: selectedRoom.id,
        check_in: formData.check_in,
        check_out: formData.check_out,
        num_guests: formData.num_guests,
        meal_plan: formData.meal_plan,
      });
      if (response.data.success) {
        setPriceData(response.data);
      }
    } catch (error) {
      console.error('Error calculating price:', error);
      setPriceData(null);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      }
    } finally {
      setCalculating(false);
    }
  };

  // Get min and max guests based on meal plan
  const getGuestLimits = () => {
    if (!selectedRoom) return { min: 1, max: 4 };
    return {
      min: 1,
      max: selectedRoom.max_guests
    };
  };

  // Handle meal plan change
  const handleMealPlanChange = (newPlan) => {
    // Don't allow switching to with_meals if guests < min occupancy
    if (newPlan === 'with_meals' && formData.num_guests < mealPricing.min_occupancy) {
      toast.error(`Room + 4 Meals requires minimum ${mealPricing.min_occupancy} guests`);
      return;
    }
    setFormData(prev => ({ ...prev, meal_plan: newPlan }));
    setPriceData(null);
  };

  // Handle guest count change with validation
  const handleGuestChange = (newCount) => {
    if (!selectedRoom) return;
    
    // Enforce room max capacity
    let validCount = Math.max(1, Math.min(selectedRoom.max_guests, newCount));
    
    // If reducing below min occupancy while on meal plan, switch to room only
    if (formData.meal_plan === 'with_meals' && validCount < mealPricing.min_occupancy) {
      setFormData(prev => ({ ...prev, num_guests: validCount, meal_plan: 'room_only' }));
      toast.info('Switched to Room Only (minimum guests for Room + 4 Meals is ' + mealPricing.min_occupancy + ')');
    } else {
      setFormData(prev => ({ ...prev, num_guests: validCount }));
    }
    setPriceData(null);
  };

  const handleSubmitBooking = async () => {
    if (!formData.check_in || !formData.check_out) {
      toast.error('Please select check-in and check-out dates');
      return;
    }
    if (!formData.guest_name || !formData.guest_email || !formData.guest_phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    setBooking(true);
    try {
      const response = await axios.post(`${API}/public/bookings`, {
        room_id: selectedRoom.id,
        check_in: formData.check_in,
        check_out: formData.check_out,
        num_guests: formData.num_guests,
        meal_plan: formData.meal_plan,
        guest_name: formData.guest_name,
        guest_email: formData.guest_email,
        guest_phone: formData.guest_phone,
        special_requests: formData.special_requests,
      });

      if (response.data.success) {
        window.location.href = `/booking-confirmation/${response.data.booking.id}`;
      }
    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setBooking(false);
    }
  };

  const openLightbox = (index, type = 'photo') => {
    setCurrentMediaIndex(index);
    setLightboxType(type);
    setLightboxOpen(true);
  };

  const closeLightbox = () => setLightboxOpen(false);

  const nextMedia = () => {
    const items = lightboxType === 'photo' ? photos : videos;
    setCurrentMediaIndex((prev) => (prev + 1) % items.length);
  };

  const prevMedia = () => {
    const items = lightboxType === 'photo' ? photos : videos;
    setCurrentMediaIndex((prev) => (prev - 1 + items.length) % items.length);
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        size={18}
        className={i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}
      />
    ));
  };

  const renderClickableStars = (rating, setRating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        size={28}
        className={`cursor-pointer transition-colors ${i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-500 hover:text-yellow-300'}`}
        onClick={() => setRating(i + 1)}
      />
    ));
  };

  const handleSubmitReview = async () => {
    if (!reviewForm.guest_name || !reviewForm.review_text) {
      toast.error('Please fill in your name and review');
      return;
    }

    setSubmittingReview(true);
    try {
      const response = await axios.post(`${API}/public/reviews`, {
        guest_name: reviewForm.guest_name,
        rating: reviewForm.rating,
        review_text: reviewForm.review_text,
        source: 'website',
      });

      if (response.data.success) {
        toast.success('Thank you for your review! It will appear after approval.');
        setShowReviewForm(false);
        setReviewForm({ guest_name: '', rating: 5, review_text: '' });
        fetchAllData();
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error('Failed to submit review. Please try again.');
    } finally {
      setSubmittingReview(false);
    }
  };

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    setMobileMenuOpen(false);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-green-500 border-t-transparent mx-auto"></div>
          <p className="mt-4 text-white text-lg">Loading Himali Khim Homestay...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="public-booking-container">
      <Toaster />
      
      {/* Fixed Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-md border-b border-gray-800">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_98d136aa-512d-4c06-970a-a610b1d5ea68/artifacts/gzoc7xht_Primary%20on%20transparent.png"
                alt="Himali Khim Homestay" 
                className="h-16 md:h-20 object-contain cursor-pointer"
                onClick={() => scrollToSection('home')}
              />
              <a
                href="tel:+917029069077"
                className="flex items-center gap-1.5 text-green-400 hover:text-green-300 text-sm font-semibold transition-colors"
                data-testid="nav-phone-always"
              >
                <Phone size={14} />
                <span>+91 70290 69077</span>
              </a>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {['home', 'about', 'rooms', 'gallery', 'reviews', 'contact'].map((section) => (
                <button
                  key={section}
                  onClick={() => scrollToSection(section)}
                  className={`text-sm font-medium transition-colors capitalize ${
                    activeSection === section ? 'text-green-400' : 'text-gray-300 hover:text-white'
                  }`}
                >
                  {section}
                </button>
              ))}
              <a
                href="https://wa.me/917029069077?text=Hi%2C%20I%20would%20like%20to%20inquire%20about%20booking%20at%20Himali%20Khim%20Homestay"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 bg-green-500 hover:bg-green-600 text-white text-sm font-medium px-3 py-2 rounded-md transition-colors"
                data-testid="nav-whatsapp-btn"
              >
                <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                WhatsApp
              </a>
              <a
                href="tel:+917029069077"
                className="flex items-center gap-1.5 bg-gray-700 hover:bg-gray-600 text-white text-sm font-medium px-3 py-2 rounded-md transition-colors"
                data-testid="nav-call-btn"
              >
                <Phone size={14} />
                Call
              </a>
              <Button 
                onClick={() => scrollToSection('rooms')}
                className="bg-green-600 hover:bg-green-700 text-white px-6"
              >
                Book Now
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-800">
              {['home', 'about', 'rooms', 'gallery', 'reviews', 'contact'].map((section) => (
                <button
                  key={section}
                  onClick={() => scrollToSection(section)}
                  className="block w-full text-left py-3 px-4 text-gray-300 hover:text-white hover:bg-gray-800 capitalize"
                >
                  {section}
                </button>
              ))}
              <div className="flex gap-2 mt-4">
                <a
                  href="https://wa.me/917029069077?text=Hi%2C%20I%20would%20like%20to%20inquire%20about%20booking%20at%20Himali%20Khim%20Homestay"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-medium py-3 rounded-md transition-colors"
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  WhatsApp
                </a>
                <a
                  href="tel:+917029069077"
                  className="flex-1 flex items-center justify-center gap-2 bg-gray-700 hover:bg-gray-600 text-white font-medium py-3 rounded-md transition-colors"
                >
                  <Phone size={18} />
                  Call
                </a>
              </div>
              <Button 
                onClick={() => scrollToSection('rooms')}
                className="w-full mt-2 bg-green-600 hover:bg-green-700"
              >
                Book Now
              </Button>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center pt-16">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1717399393413-865590439e08?w=1920&q=90')`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black"></div>
        </div>
        
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 animate-fade-in">
            <span className="text-green-400">Himali Khim</span> Homestay
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 mb-4 font-light">
            Peaceful Stay in Upper Sittong, Darjeeling
          </p>
          <p className="text-lg md:text-xl text-green-400 mb-8 tracking-widest">
            OFFBEAT. UNSPOILED. UNFORGETTABLE.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => scrollToSection('rooms')}
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-lg"
            >
              Book Your Stay <ArrowRight className="ml-2" size={20} />
            </Button>
            <Button 
              variant="outline"
              onClick={() => scrollToSection('gallery')}
              className="border-white text-white hover:bg-white hover:text-black px-8 py-6 text-lg"
            >
              View Gallery <Eye className="ml-2" size={20} />
            </Button>
          </div>
          
          <div className="mt-12 flex items-center justify-center gap-2">
            <div className="flex">{renderStars(5)}</div>
            <span className="text-gray-300 ml-2">5.0 Rating • {reviews.length} Reviews</span>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-white/50 rounded-full animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Welcome to <span className="text-green-400">Himali Khim</span>
              </h2>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                Himali Khim Homestay is a comfortable, family-run homestay located in <strong className="text-white">Gyaletar, Upper Sittong</strong>, surrounded by lush greenery and serene mountain views. Ideal for couples, families, and small groups, the homestay offers a calm retreat away from city life with warm hospitality and personalized care.
              </p>
              <p className="text-gray-300 text-lg leading-relaxed mb-8">
                The newfoundland of Sittong is a tiny, mesmerizing village situated just miles away from Darjeeling. Sittong is a part of the offbeat Darjeeling region and is home to several birds, animals, unseen flora, and top-notch hotels.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Mountain, text: 'Mountain Views' },
                  { icon: Utensils, text: 'Home-cooked Meals' },
                  { icon: Wifi, text: 'Free WiFi' },
                  { icon: Users, text: 'Family Friendly' },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-4 bg-gray-800/50 rounded-lg">
                    <item.icon className="text-green-400" size={24} />
                    <span className="text-gray-200">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              {photos.slice(0, 4).map((photo, idx) => (
                <div 
                  key={photo.id}
                  className={`relative overflow-hidden rounded-xl cursor-pointer group ${idx === 0 ? 'col-span-2 h-64' : 'h-48'}`}
                  onClick={() => openLightbox(idx, 'photo')}
                >
                  <img
                    src={getImageUrl(photo.file_url)}
                    alt={photo.original_filename}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center">
                    <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={40} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Facilities Section */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4 max-w-7xl">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            Facilities at <span className="text-green-400">Himali Khim</span>
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            Essential comforts to ensure a pleasant and memorable stay
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            {[
              { icon: Mountain, title: 'Pristine Location' },
              { icon: Bed, title: 'Large Rooms' },
              { icon: Utensils, title: 'Homely Food' },
              { icon: Bath, title: 'Attached Bathrooms' },
              { icon: Wifi, title: 'Free WiFi' },
              { icon: Car, title: 'Free Parking' },
              { icon: Eye, title: 'Mountain View' },
              { icon: ChefHat, title: 'Full Kitchen' },
              { icon: Flame, title: 'Electric Heated Mattress' },
              { icon: Droplets, title: 'Geyser' },
              { icon: Sparkles, title: 'BBQ & Bonfire' },
              { icon: Thermometer, title: 'Room Heater' },
            ].map((item, idx) => (
              <div 
                key={idx} 
                className="flex items-center gap-2 px-4 py-2 bg-gray-800/70 rounded-full border border-gray-700"
              >
                <item.icon className="text-green-400" size={18} />
                <span className="text-gray-200 text-sm">{item.title}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            Why Choose <span className="text-green-400">Himali Khim?</span>
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            Experience the perfect blend of comfort, nature, and authentic hospitality
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4">
            <div className="flex items-start gap-3">
              <Check className="text-green-400 mt-1 flex-shrink-0" size={20} />
              <span className="text-gray-300">Peaceful location in Upper Sittong with stunning Kanchenjunga views</span>
            </div>
            <div className="flex items-start gap-3">
              <Check className="text-green-400 mt-1 flex-shrink-0" size={20} />
              <span className="text-gray-300">Clean, spacious rooms with modern amenities</span>
            </div>
            <div className="flex items-start gap-3">
              <Check className="text-green-400 mt-1 flex-shrink-0" size={20} />
              <span className="text-gray-300">Fresh, organic home-cooked local cuisine</span>
            </div>
            <div className="flex items-start gap-3">
              <Check className="text-green-400 mt-1 flex-shrink-0" size={20} />
              <span className="text-gray-300">Personalized care from attentive hosts</span>
            </div>
            <div className="flex items-start gap-3">
              <Check className="text-green-400 mt-1 flex-shrink-0" size={20} />
              <span className="text-gray-300">Nature walks through orange orchards & scenic viewpoints</span>
            </div>
            <div className="flex items-start gap-3">
              <Check className="text-green-400 mt-1 flex-shrink-0" size={20} />
              <span className="text-gray-300">Authentic village life & cultural experience</span>
            </div>
          </div>
        </div>
      </section>

      {/* Rooms Section */}
      <section id="rooms" className="py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="container mx-auto px-4 max-w-7xl">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            Our <span className="text-green-400">Accommodations</span>
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            Choose from our comfortable rooms with stunning mountain views
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {rooms.map((room) => (
              <Card 
                key={room.id} 
                className="bg-gray-800/50 border-gray-700 overflow-hidden hover:border-green-500/50 transition-all group"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={getImageUrl(room.image_url)}
                    alt={room.name}
                    onError={handleImageError}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute top-4 right-4 bg-green-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    ₹{room.base_price}/night
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-white mb-2">{room.name}</h3>
                  <p className="text-gray-400 text-sm mb-4 flex items-center gap-2">
                    <Users size={16} /> Up to {room.max_guests} guests
                  </p>
                  <p className="text-gray-300 text-sm mb-4 line-clamp-2">{room.description}</p>
                  
                  <div className="flex flex-wrap gap-2 mb-4">
                    {room.amenities?.slice(0, 3).map((amenity, idx) => (
                      <span key={idx} className="text-xs bg-gray-700 text-gray-300 px-2 py-1 rounded">
                        {amenity}
                      </span>
                    ))}
                  </div>

                  <div className="border-t border-gray-700 pt-4 mt-4">
                    <div className="space-y-3 mb-4">
                      <div className="flex justify-between items-center p-2 bg-gray-800/50 rounded">
                        <span className="text-gray-300 text-sm font-medium">Room Only</span>
                        <span className="text-xl font-bold text-white">₹{room.base_price}<span className="text-xs font-normal text-gray-400">/night</span></span>
                      </div>
                      <div className="p-3 bg-green-900/30 rounded border border-green-700/50">
                        <div className="text-green-300 text-sm font-medium mb-2">Room + Food - Rate/Person/Day</div>
                        <div className="text-sm text-white space-y-1">
                          <div className="flex justify-between">
                            <span>2 Person</span>
                            <span className="font-semibold">₹{mealPricing.meal_price_2_persons || 1600}/Person</span>
                          </div>
                          <div className="flex justify-between">
                            <span>3 Person</span>
                            <span className="font-semibold">₹{mealPricing.meal_price_3_persons || 1500}/Person</span>
                          </div>
                          <div className="flex justify-between">
                            <span>4 Person</span>
                            <span className="font-semibold">₹{mealPricing.meal_price_4_plus || 1400}/Person</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {/* Central Booking Section */}
          <div id="book-now-section" className="mt-16 max-w-4xl mx-auto">
            <Card className="bg-gradient-to-br from-green-900/80 to-gray-900 border-green-700">
              <CardHeader className="text-center">
                <CardTitle className="text-3xl font-bold text-white">
                  Book Your <span className="text-green-400">Stay</span>
                </CardTitle>
                <p className="text-gray-300 mt-2">Select your dates and preferred room to begin your mountain getaway</p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Date Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white">Check-in Date *</Label>
                    <Input
                      type="date"
                      value={bookingDates.checkIn}
                      onChange={(e) => {
                        setBookingDates({ ...bookingDates, checkIn: e.target.value });
                        setSelectedRoom(null);
                      }}
                      min={new Date().toISOString().split('T')[0]}
                      className="bg-white/10 border-gray-600 text-white"
                      data-testid="central-checkin-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Check-out Date *</Label>
                    <Input
                      type="date"
                      value={bookingDates.checkOut}
                      onChange={(e) => {
                        setBookingDates({ ...bookingDates, checkOut: e.target.value });
                        setSelectedRoom(null);
                      }}
                      min={bookingDates.checkIn || new Date().toISOString().split('T')[0]}
                      className="bg-white/10 border-gray-600 text-white"
                      data-testid="central-checkout-date"
                    />
                  </div>
                </div>

                {/* Package Selection */}
                {bookingDates.checkIn && bookingDates.checkOut && (
                  <div className="space-y-2">
                    <Label className="text-white">Select Package *</Label>
                    <div className="grid grid-cols-2 gap-4">
                      <div
                        onClick={() => setFormData(prev => ({ ...prev, meal_plan: 'room_only' }))}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          formData.meal_plan === 'room_only'
                            ? 'border-green-500 bg-green-900/50'
                            : 'border-gray-600 bg-white/5 hover:border-gray-500'
                        }`}
                        data-testid="package-room-only"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <Bed className="text-green-400" size={20} />
                          <span className="font-semibold text-white">Room Only</span>
                        </div>
                        <p className="text-sm text-gray-400">Accommodation without meals</p>
                      </div>
                      <div
                        onClick={() => {
                          setFormData(prev => ({ ...prev, meal_plan: 'with_meals' }));
                          // Ensure minimum 2 guests for meals package
                          if (centralGuestCount < 2) {
                            setCentralGuestCount(2);
                          }
                        }}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          formData.meal_plan === 'with_meals'
                            ? 'border-green-500 bg-green-900/50'
                            : 'border-gray-600 bg-white/5 hover:border-gray-500'
                        }`}
                        data-testid="package-with-meals"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <Utensils className="text-amber-400" size={20} />
                          <span className="font-semibold text-white">Room + Meals</span>
                        </div>
                        <p className="text-sm text-gray-400">Breakfast, Lunch, Tea & Dinner</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Available Rooms Dropdown - Now BEFORE guest count */}
                {bookingDates.checkIn && bookingDates.checkOut && (
                  <div className="space-y-2">
                    <Label className="text-white flex items-center gap-2">
                      Select Room *
                      {loadingAvailableRooms && <span className="text-green-400 text-sm">(checking availability...)</span>}
                    </Label>
                    {availableRoomsForBooking.length > 0 ? (
                      <Select
                        value={selectedRoom?.id || ''}
                        onValueChange={(value) => {
                          const room = availableRoomsForBooking.find(r => r.id === value);
                          setSelectedRoom(room);
                          // Reset guest count to appropriate minimum/maximum for the room
                          if (room) {
                            const minGuests = formData.meal_plan === 'with_meals' ? 2 : 1;
                            const maxGuests = room.max_guests;
                            // Set to minimum if current count is less, or max if exceeds
                            if (centralGuestCount < minGuests) {
                              setCentralGuestCount(minGuests);
                            } else if (centralGuestCount > maxGuests) {
                              setCentralGuestCount(maxGuests);
                            }
                          }
                        }}
                      >
                        <SelectTrigger className="bg-white/10 border-gray-600 text-white" data-testid="central-room-select">
                          <SelectValue placeholder="Choose an available room" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableRoomsForBooking.map((room) => (
                            <SelectItem key={room.id} value={room.id}>
                              {room.name} (Max {room.max_guests} guests)
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : !loadingAvailableRooms ? (
                      <div className="bg-red-900/50 border border-red-500 rounded-lg p-4 text-center">
                        <p className="text-red-300">Sorry, all rooms are booked for these dates.</p>
                        <p className="text-sm text-red-400 mt-1">Please try different dates.</p>
                      </div>
                    ) : null}
                  </div>
                )}

                {/* Number of Guests Selection - Now AFTER room selection */}
                {bookingDates.checkIn && bookingDates.checkOut && selectedRoom && (
                  <div className="space-y-2">
                    <Label className="text-white">Number of Guests *</Label>
                    <Select
                      value={centralGuestCount.toString()}
                      onValueChange={(value) => setCentralGuestCount(parseInt(value))}
                    >
                      <SelectTrigger className="bg-white/10 border-gray-600 text-white" data-testid="central-guest-count">
                        <SelectValue placeholder="Select guests" />
                      </SelectTrigger>
                      <SelectContent>
                        {(() => {
                          const minGuests = formData.meal_plan === 'with_meals' ? 2 : 1;
                          const maxGuests = selectedRoom.max_guests;
                          const guestOptions = [];
                          for (let i = minGuests; i <= maxGuests; i++) {
                            guestOptions.push(
                              <SelectItem key={i} value={i.toString()}>
                                {i} Guest{i > 1 ? 's' : ''}
                              </SelectItem>
                            );
                          }
                          return guestOptions;
                        })()}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-400">
                      {formData.meal_plan === 'with_meals' 
                        ? `Min 2 guests, Max ${selectedRoom.max_guests} guests for this room`
                        : `Min 1 guest, Max ${selectedRoom.max_guests} guests for this room`
                      }
                    </p>
                  </div>
                )}

                {/* Price Preview & Book Button */}
                {selectedRoom && bookingDates.checkIn && bookingDates.checkOut && (() => {
                  // Calculate number of nights
                  const checkIn = new Date(bookingDates.checkIn);
                  const checkOut = new Date(bookingDates.checkOut);
                  const numNights = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
                  
                  // Get meal rate based on guest count (this is all-inclusive per person rate)
                  const getMealRate = (guests) => {
                    if (guests <= 2) return mealPricing.meal_price_2_persons;
                    if (guests === 3) return mealPricing.meal_price_3_persons;
                    return mealPricing.meal_price_4_plus;
                  };
                  
                  // Calculate total price
                  let totalPrice = 0;
                  let roomCost = selectedRoom.base_price * numNights;
                  
                  if (formData.meal_plan === 'room_only') {
                    // Room Only: Room cost × Number of nights
                    totalPrice = roomCost;
                  } else {
                    // Room + Meals: Guests × Nights × Package rate per person (all-inclusive, no separate room cost)
                    const packageRatePerPerson = getMealRate(centralGuestCount);
                    totalPrice = centralGuestCount * numNights * packageRatePerPerson;
                  }
                  
                  const advanceAmount = Math.round(totalPrice * 0.5);
                  const balanceAmount = totalPrice - advanceAmount;
                  
                  return (
                    <div className="bg-white/10 rounded-lg p-4 space-y-4">
                      <div className="flex items-center justify-between border-b border-gray-700 pb-3">
                        <div>
                          <h4 className="text-white font-semibold">{selectedRoom.name}</h4>
                          <p className="text-gray-400 text-sm">{centralGuestCount} Guest{centralGuestCount > 1 ? 's' : ''} • {numNights} Night{numNights > 1 ? 's' : ''}</p>
                          <p className="text-green-400 text-sm mt-1">
                            {formData.meal_plan === 'room_only' ? '🛏️ Room Only' : '🍽️ Room + Meals'}
                          </p>
                        </div>
                      </div>
                      
                      {/* Price Breakdown */}
                      <div className="space-y-2 text-sm">
                        {formData.meal_plan === 'room_only' ? (
                          <div className="flex justify-between text-gray-300">
                            <span>Room ({numNights} night{numNights > 1 ? 's' : ''} × ₹{selectedRoom.base_price})</span>
                            <span>₹{roomCost.toLocaleString()}</span>
                          </div>
                        ) : (
                          <div className="flex justify-between text-amber-300">
                            <span>Room + Meals ({centralGuestCount} guests × {numNights} night{numNights > 1 ? 's' : ''} × ₹{getMealRate(centralGuestCount)}/person)</span>
                            <span>₹{totalPrice.toLocaleString()}</span>
                          </div>
                        )}
                        <div className="flex justify-between border-t border-gray-700 pt-2 text-white font-bold">
                          <span>Total Amount</span>
                          <span className="text-xl text-green-400">₹{totalPrice.toLocaleString()}</span>
                        </div>
                      </div>
                      
                      {/* 50% Advance Payment */}
                      <div className="bg-green-900/30 border border-green-700 rounded-lg p-3">
                        <p className="text-green-400 text-sm font-semibold mb-2">💳 Payment (50% Advance)</p>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <p className="text-gray-400">Pay Now</p>
                            <p className="text-green-400 font-bold">₹{advanceAmount.toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-gray-400">At Check-in</p>
                            <p className="text-orange-400 font-bold">₹{balanceAmount.toLocaleString()}</p>
                          </div>
                        </div>
                      </div>
                      
                      <Button 
                        className="w-full bg-green-600 hover:bg-green-700 text-lg py-6"
                        onClick={() => {
                          // Sync all data with booking form
                          setFormData(prev => ({
                            ...prev,
                            check_in: bookingDates.checkIn,
                            check_out: bookingDates.checkOut,
                            num_guests: centralGuestCount
                          }));
                          // Set calendar dates for display
                          setCheckInDate(new Date(bookingDates.checkIn));
                          setCheckOutDate(new Date(bookingDates.checkOut));
                          setShowBookingForm(true);
                        }}
                        data-testid="central-book-button"
                      >
                        Continue to Book • ₹{advanceAmount.toLocaleString()} Advance
                      </Button>
                    </div>
                  );
                })()}

                {/* Instructions */}
                {!bookingDates.checkIn || !bookingDates.checkOut ? (
                  <div className="text-center text-gray-400">
                    <p>👆 Please select your check-in and check-out dates to see available rooms</p>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Photo Gallery Section */}
      <section id="gallery" className="py-20 bg-black">
        <div className="container mx-auto px-4 max-w-7xl">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            Photo <span className="text-green-400">Gallery</span>
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            Take a virtual tour of our beautiful homestay
          </p>

          {/* Horizontal Scrollable Photo Gallery */}
          <div className="relative group/gallery">
            {/* Left Arrow */}
            <button
              onClick={() => {
                const container = document.getElementById('photo-scroll-container');
                container.scrollBy({ left: -320, behavior: 'smooth' });
              }}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-black/70 hover:bg-green-600 text-white p-3 rounded-full shadow-lg transition-all opacity-0 group-hover/gallery:opacity-100 -ml-4"
              data-testid="photo-scroll-left"
            >
              <ChevronLeft size={24} />
            </button>

            {/* Scrollable Container */}
            <div
              id="photo-scroll-container"
              className="flex gap-4 overflow-x-auto scroll-smooth pb-4 scrollbar-hide"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              onTouchStart={(e) => {
                e.currentTarget.dataset.touchStartX = e.touches[0].clientX;
              }}
              onTouchEnd={(e) => {
                const startX = parseFloat(e.currentTarget.dataset.touchStartX);
                const endX = e.changedTouches[0].clientX;
                const diff = startX - endX;
                if (Math.abs(diff) > 50) {
                  e.currentTarget.scrollBy({ left: diff > 0 ? 320 : -320, behavior: 'smooth' });
                }
              }}
            >
              {photos.map((photo, index) => (
                <div 
                  key={photo.id}
                  className="flex-shrink-0 w-72 md:w-80 relative overflow-hidden rounded-xl cursor-pointer group"
                  onClick={() => openLightbox(index, 'photo')}
                  data-testid={`gallery-photo-${index}`}
                >
                  <img
                    src={getImageUrl(photo.file_url)}
                    alt={photo.original_filename}
                    onError={handleImageError}
                    className="w-full h-56 md:h-64 object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute bottom-4 left-4 right-4">
                      <p className="text-white text-sm truncate">{photo.original_filename}</p>
                    </div>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" size={40} />
                  </div>
                </div>
              ))}
            </div>

            {/* Right Arrow */}
            <button
              onClick={() => {
                const container = document.getElementById('photo-scroll-container');
                container.scrollBy({ left: 320, behavior: 'smooth' });
              }}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-black/70 hover:bg-green-600 text-white p-3 rounded-full shadow-lg transition-all opacity-0 group-hover/gallery:opacity-100 -mr-4"
              data-testid="photo-scroll-right"
            >
              <ChevronRight size={24} />
            </button>
          </div>

          {/* Video Gallery */}
          {videos.length > 0 && (
            <div className="mt-16">
              <h3 className="text-2xl font-bold text-center mb-8">
                <Play className="inline mr-2 text-green-400" size={28} />
                Video <span className="text-green-400">Gallery</span>
              </h3>
              
              {/* Horizontal Scrollable Video Gallery */}
              <div className="relative group/videos">
                {/* Left Arrow */}
                <button
                  onClick={() => {
                    const container = document.getElementById('video-scroll-container');
                    container.scrollBy({ left: -320, behavior: 'smooth' });
                  }}
                  className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-black/70 hover:bg-green-600 text-white p-3 rounded-full shadow-lg transition-all opacity-0 group-hover/videos:opacity-100 -ml-4"
                  data-testid="video-scroll-left"
                >
                  <ChevronLeft size={24} />
                </button>

                {/* Scrollable Container */}
                <div
                  id="video-scroll-container"
                  className="flex gap-4 overflow-x-auto scroll-smooth pb-4 scrollbar-hide"
                  style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                  onTouchStart={(e) => {
                    e.currentTarget.dataset.touchStartX = e.touches[0].clientX;
                  }}
                  onTouchEnd={(e) => {
                    const startX = parseFloat(e.currentTarget.dataset.touchStartX);
                    const endX = e.changedTouches[0].clientX;
                    const diff = startX - endX;
                    if (Math.abs(diff) > 50) {
                      e.currentTarget.scrollBy({ left: diff > 0 ? 320 : -320, behavior: 'smooth' });
                    }
                  }}
                >
                  {videos.map((video, index) => (
                    <div 
                      key={video.id}
                      className="flex-shrink-0 w-72 md:w-80 relative rounded-xl overflow-hidden group cursor-pointer"
                      onClick={() => openLightbox(index, 'video')}
                      data-testid={`gallery-video-${index}`}
                    >
                      {/* Video thumbnail - use image thumbnail for Google Drive, video element for local */}
                      {isGoogleDriveVideo(video.file_url) ? (
                        <div className="w-full h-48 bg-gray-800 flex items-center justify-center">
                          <img 
                            src={`https://drive.google.com/thumbnail?id=${extractGoogleDriveFileId(video.file_url)}&sz=w400`}
                            alt={video.original_filename}
                            className="w-full h-48 object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      ) : (
                        <video
                          src={getVideoUrl(video.file_url)}
                          className="w-full h-48 object-cover"
                          muted
                        />
                      )}
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center group-hover:bg-black/30 transition-colors">
                        <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                          <Play className="text-white ml-1" size={28} />
                        </div>
                      </div>
                      <p className="absolute bottom-2 left-2 text-white text-sm bg-black/50 px-2 py-1 rounded">
                        {video.original_filename}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Right Arrow */}
                <button
                  onClick={() => {
                    const container = document.getElementById('video-scroll-container');
                    container.scrollBy({ left: 320, behavior: 'smooth' });
                  }}
                  className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-black/70 hover:bg-green-600 text-white p-3 rounded-full shadow-lg transition-all opacity-0 group-hover/videos:opacity-100 -mr-4"
                  data-testid="video-scroll-right"
                >
                  <ChevronRight size={24} />
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Reviews Section */}
      <section id="reviews" className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-4 max-w-7xl">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            Guest <span className="text-green-400">Reviews</span>
          </h2>
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="flex">{renderStars(5)}</div>
            <span className="text-2xl font-bold text-white">5.0</span>
            <span className="text-gray-400">({reviews.length} reviews)</span>
          </div>

          {/* Write a Review Button */}
          <div className="text-center mb-12">
            <Button
              onClick={() => setShowReviewForm(true)}
              className="bg-green-600 hover:bg-green-700 px-8 py-3"
            >
              <Star className="mr-2" size={20} />
              Write a Review
            </Button>
          </div>

          {/* Horizontal Scrollable Reviews */}
          <div className="relative group/reviews">
            {/* Left Arrow */}
            <button
              onClick={() => {
                const container = document.getElementById('reviews-scroll-container');
                container.scrollBy({ left: -380, behavior: 'smooth' });
              }}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-black/70 hover:bg-green-600 text-white p-3 rounded-full shadow-lg transition-all opacity-0 group-hover/reviews:opacity-100 -ml-4"
              data-testid="reviews-scroll-left"
            >
              <ChevronLeft size={24} />
            </button>

            {/* Scrollable Container */}
            <div
              id="reviews-scroll-container"
              className="flex gap-6 overflow-x-auto scroll-smooth pb-4 scrollbar-hide"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              onTouchStart={(e) => {
                e.currentTarget.dataset.touchStartX = e.touches[0].clientX;
              }}
              onTouchEnd={(e) => {
                const startX = parseFloat(e.currentTarget.dataset.touchStartX);
                const endX = e.changedTouches[0].clientX;
                const diff = startX - endX;
                if (Math.abs(diff) > 50) {
                  e.currentTarget.scrollBy({ left: diff > 0 ? 380 : -380, behavior: 'smooth' });
                }
              }}
            >
              {reviews.map((review, index) => (
                <Card 
                  key={review.id} 
                  className="flex-shrink-0 w-80 md:w-96 bg-gray-800/50 border-gray-700 p-6 cursor-pointer hover:bg-gray-800/70 transition-colors"
                  data-testid={`review-card-${index}`}
                  onClick={() => setExpandedReviews(prev => ({ ...prev, [review.id]: !prev[review.id] }))}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-700 rounded-full flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                      {review.guest_name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-white text-lg truncate">{review.guest_name}</h4>
                        <div className="flex flex-shrink-0">{renderStars(review.rating)}</div>
                      </div>
                      <p className={`text-gray-300 leading-relaxed mb-3 ${expandedReviews[review.id] ? '' : 'line-clamp-4'}`}>{review.review_text}</p>
                      {review.review_text.length > 150 && (
                        <button className="text-green-400 text-sm mb-2 hover:text-green-300">
                          {expandedReviews[review.id] ? 'Show less' : 'Read more...'}
                        </button>
                      )}
                      <div className="flex items-center gap-2 text-sm text-gray-500 flex-wrap">
                        <span>{new Date(review.review_date).toLocaleDateString('en-US', { 
                          year: 'numeric', month: 'short', day: 'numeric' 
                        })}</span>
                        {review.source === 'google' && (
                          <span className="bg-blue-900/50 text-blue-300 px-2 py-0.5 rounded text-xs">
                            Google Review
                          </span>
                        )}
                        {review.source === 'website' && (
                          <span className="bg-green-900/50 text-green-300 px-2 py-0.5 rounded text-xs">
                            Website Review
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Right Arrow */}
            <button
              onClick={() => {
                const container = document.getElementById('reviews-scroll-container');
                container.scrollBy({ left: 380, behavior: 'smooth' });
              }}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-black/70 hover:bg-green-600 text-white p-3 rounded-full shadow-lg transition-all opacity-0 group-hover/reviews:opacity-100 -mr-4"
              data-testid="reviews-scroll-right"
            >
              <ChevronRight size={24} />
            </button>
          </div>
        </div>
      </section>

      {/* Review Submission Modal */}
      <Dialog open={showReviewForm} onOpenChange={setShowReviewForm}>
        <DialogContent className="max-w-lg bg-gray-900 border-gray-700 text-white">
          <h2 className="text-2xl font-bold mb-2">
            Share Your <span className="text-green-400">Experience</span>
          </h2>
          <p className="text-gray-400 mb-6">We&apos;d love to hear about your stay at Himali Khim Homestay!</p>
          
          <div className="space-y-5">
            <div>
              <Label className="text-gray-300 mb-2 block">Your Name *</Label>
              <Input
                value={reviewForm.guest_name}
                onChange={(e) => setReviewForm({ ...reviewForm, guest_name: e.target.value })}
                placeholder="Enter your full name"
                className="bg-gray-800 border-gray-600 text-white"
              />
            </div>

            <div>
              <Label className="text-gray-300 mb-3 block">Your Rating *</Label>
              <div className="flex gap-2">
                {renderClickableStars(reviewForm.rating, (rating) => setReviewForm({ ...reviewForm, rating }))}
              </div>
            </div>

            <div>
              <Label className="text-gray-300 mb-2 block">Your Review *</Label>
              <Textarea
                value={reviewForm.review_text}
                onChange={(e) => setReviewForm({ ...reviewForm, review_text: e.target.value })}
                placeholder="Tell us about your experience... What did you enjoy most about your stay?"
                className="bg-gray-800 border-gray-600 text-white"
                rows={5}
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowReviewForm(false)}
                className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-800"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmitReview}
                disabled={submittingReview}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                {submittingReview ? 'Submitting...' : 'Submit Review'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* How to Reach Section */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4 max-w-7xl">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            How to <span className="text-green-400">Reach Us</span>
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            Multiple convenient ways to reach Himali Khim Homestay
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { 
                icon: Car, 
                title: 'By Road', 
                desc: 'Himali Khim homestay can be reached through the Hill Cart Roadside or from the Teesta side. People visiting from NJP or Bagdogra should take the Teesta road route.' 
              },
              { 
                icon: Train, 
                title: 'By Train', 
                desc: 'Nearest railway station is New Jalpaiguri (NJP). It takes about 2 hours to reach Sittong from the station. Taxis and buses are available.' 
              },
              { 
                icon: Plane, 
                title: 'By Air', 
                desc: 'The Bagdogra Airport is the nearest airport. From the airport, cabs are available that can take you to the homestay in about 2 hours.' 
              },
            ].map((item, idx) => (
              <div key={idx} className="text-center p-8 bg-gray-800/50 rounded-xl border border-gray-700">
                <div className="w-16 h-16 bg-green-600/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <item.icon className="text-green-400" size={32} />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">{item.title}</h3>
                <p className="text-gray-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Get in <span className="text-green-400">Touch</span>
              </h2>
              <p className="text-gray-400 mb-8">
                Ready to experience the serenity of Himali Khim? Contact us for bookings and inquiries.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="text-green-400" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">Phone</h4>
                    <a href="tel:+917029069077" className="text-gray-400 hover:text-green-400 text-lg">
                      +91 70290 69077
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="text-green-400" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">Email</h4>
                    <a href="mailto:himalikhim@gmail.com" className="text-gray-400 hover:text-green-400 text-lg">
                      himalikhim@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-green-400" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">Address</h4>
                    <p className="text-gray-400">
                      Khasmahal, Gyaletar, Upper Sittong,<br />
                      Darjeeling District, West Bengal – 734313
                    </p>
                  </div>
                </div>
              </div>

              {/* Social QR Codes */}
              <div className="mt-8 flex gap-6">
                <div className="text-center">
                  <div className="bg-white p-3 rounded-lg inline-block mb-2">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_homestay-admin/artifacts/dn9b3een_QR_Facebook_Himali%20Khim%20Homestay_Nobin.png" 
                      alt="Facebook QR"
                      className="w-24 h-24 object-contain"
                    />
                  </div>
                  <p className="text-xs text-gray-500">Follow on Facebook</p>
                </div>
                <div className="text-center">
                  <div className="bg-white p-3 rounded-lg inline-block mb-2">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_homestay-admin/artifacts/ixnzqpwa_QR_Google_Himali%20Khim%20Homestay_Google.jpg" 
                      alt="Google Location QR"
                      className="w-24 h-24 object-contain"
                    />
                  </div>
                  <p className="text-xs text-gray-500">Find on Google Maps</p>
                </div>
              </div>
            </div>

            {/* WhatsApp Contact Card */}
            <div className="bg-gray-800/50 border border-gray-700 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-white mb-6">Quick Contact</h3>
              <p className="text-gray-400 mb-6">
                For bookings and inquiries, contact us directly via WhatsApp or Phone call:
              </p>
              
              <div className="space-y-4">
                <a 
                  href="https://wa.me/917029069077?text=Hi%2C%20I%20would%20like%20to%20inquire%20about%20booking%20at%20Himali%20Khim%20Homestay"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-3 w-full bg-green-600 hover:bg-green-700 text-white py-4 px-6 rounded-lg text-lg font-semibold transition-colors"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Chat on WhatsApp
                </a>
                
                <a 
                  href="tel:+917029069077"
                  className="flex items-center justify-center gap-3 w-full bg-gray-700 hover:bg-gray-600 text-white py-4 px-6 rounded-lg text-lg font-semibold transition-colors"
                >
                  <Phone size={24} />
                  Call: +91 70290 69077
                </a>
                
                <a 
                  href="mailto:himalikhim@gmail.com"
                  className="flex items-center justify-center gap-3 w-full bg-gray-700 hover:bg-gray-600 text-white py-4 px-6 rounded-lg text-lg font-semibold transition-colors"
                >
                  <Mail size={24} />
                  Email Us
                </a>
              </div>
              
              <p className="text-gray-500 text-sm mt-6 text-center">
                We typically respond within 1-2 hours during business hours
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-gray-800 py-12">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_98d136aa-512d-4c06-970a-a610b1d5ea68/artifacts/gzoc7xht_Primary%20on%20transparent.png"
              alt="Himali Khim Homestay" 
              className="h-20 object-contain"
            />
            <p className="text-gray-500 text-center">
              © 2026 Himali Khim Homestay. All rights reserved.
            </p>
            <div className="flex gap-4">
              <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-green-600 transition-colors">
                <Facebook size={20} />
              </a>
              <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-green-600 transition-colors">
                <Instagram size={20} />
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Lightbox for Photos/Videos */}
      {lightboxOpen && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" onClick={closeLightbox}>
          <button 
            className="absolute top-4 right-4 text-white hover:text-gray-300 z-50 bg-black/50 p-2 rounded-full"
            onClick={closeLightbox}
          >
            <X size={32} />
          </button>
          <button 
            className="absolute left-4 text-white hover:text-gray-300 p-2 bg-black/50 rounded-full"
            onClick={(e) => { e.stopPropagation(); prevMedia(); }}
          >
            <ChevronLeft size={48} />
          </button>
          
          {lightboxType === 'photo' ? (
            <img
              src={getImageUrl(photos[currentMediaIndex]?.file_url)}
              alt={photos[currentMediaIndex]?.original_filename}
              onError={handleImageError}
              className="max-h-[90vh] max-w-[90vw] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            // Use iframe for Google Drive videos, video tag for local files
            isGoogleDriveVideo(videos[currentMediaIndex]?.file_url) ? (
              <iframe
                src={getVideoUrl(videos[currentMediaIndex]?.file_url)}
                className="w-[90vw] h-[80vh] max-w-4xl"
                allow="autoplay; encrypted-media"
                allowFullScreen
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <video
                src={getVideoUrl(videos[currentMediaIndex]?.file_url)}
                className="max-h-[90vh] max-w-[90vw]"
                controls
                autoPlay
                onClick={(e) => e.stopPropagation()}
              />
            )
          )}
          
          <button 
            className="absolute right-4 text-white hover:text-gray-300 p-2 bg-black/50 rounded-full"
            onClick={(e) => { e.stopPropagation(); nextMedia(); }}
          >
            <ChevronRight size={48} />
          </button>
          <div className="absolute bottom-4 text-white text-sm bg-black/50 px-4 py-2 rounded-full">
            {currentMediaIndex + 1} / {lightboxType === 'photo' ? photos.length : videos.length}
          </div>
        </div>
      )}

      {/* Booking Form Modal */}
      <Dialog open={showBookingForm} onOpenChange={setShowBookingForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-gray-900 border-gray-700 text-white">
          <h2 className="text-2xl font-bold mb-4">
            Book: <span className="text-green-400">{selectedRoom?.name}</span>
          </h2>
          
          <div className="space-y-4">
            {/* Date Pickers */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300 mb-2 block">Check-in Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal bg-gray-800 border-gray-600 text-white hover:bg-gray-700"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {checkInDate ? format(checkInDate, "PPP") : <span className="text-gray-400">Select date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-gray-800 border-gray-600" align="start">
                    <Calendar
                      mode="single"
                      selected={checkInDate}
                      onSelect={(date) => {
                        setCheckInDate(date);
                        if (date) {
                          setFormData({ ...formData, check_in: format(date, 'yyyy-MM-dd') });
                        }
                      }}
                      disabled={(date) => date < new Date()}
                      initialFocus
                      className="text-white"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label className="text-gray-300 mb-2 block">Check-out Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal bg-gray-800 border-gray-600 text-white hover:bg-gray-700"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {checkOutDate ? format(checkOutDate, "PPP") : <span className="text-gray-400">Select date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-gray-800 border-gray-600" align="start">
                    <Calendar
                      mode="single"
                      selected={checkOutDate}
                      defaultMonth={checkInDate || new Date()}
                      onSelect={(date) => {
                        setCheckOutDate(date);
                        if (date) {
                          setFormData({ ...formData, check_out: format(date, 'yyyy-MM-dd') });
                        }
                      }}
                      disabled={(date) => date <= (checkInDate || new Date())}
                      initialFocus
                      className="text-white"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Number of Guests - FIRST */}
            <div>
              <Label className="text-gray-300 mb-2 block">
                Number of Guests * 
                <span className="text-gray-500 ml-2">(Max {selectedRoom?.max_guests})</span>
              </Label>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => handleGuestChange(formData.num_guests - 1)}
                  disabled={formData.num_guests <= 1}
                  className="w-10 h-10 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold"
                >
                  -
                </button>
                <div className="flex-1 text-center">
                  <span className="text-2xl font-bold text-white">{formData.num_guests}</span>
                  <span className="text-gray-400 ml-2">guest{formData.num_guests > 1 ? 's' : ''}</span>
                </div>
                <button
                  type="button"
                  onClick={() => handleGuestChange(formData.num_guests + 1)}
                  disabled={formData.num_guests >= selectedRoom?.max_guests}
                  className="w-10 h-10 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold"
                >
                  +
                </button>
              </div>
              {formData.num_guests < mealPricing.min_occupancy && (
                <p className="text-xs text-yellow-400 mt-2">
                  Note: Room + 4 Meals requires minimum {mealPricing.min_occupancy} guests
                </p>
              )}
            </div>

            {/* Booking Type Selection - SECOND */}
            <div>
              <Label className="text-gray-300 mb-2 block">Booking Type *</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => handleMealPlanChange('room_only')}
                  className={`p-4 rounded-lg border-2 text-center transition-all ${
                    formData.meal_plan === 'room_only' 
                      ? 'border-green-500 bg-green-500/20' 
                      : 'border-gray-600 bg-gray-800 hover:border-gray-500'
                  }`}
                >
                  <div className="font-semibold text-white">Room Only</div>
                  <div className="text-xs text-gray-400 mt-1">Flat rate per night</div>
                </button>
                
                <button
                  type="button"
                  onClick={() => handleMealPlanChange('with_meals')}
                  disabled={formData.num_guests < mealPricing.min_occupancy}
                  className={`p-4 rounded-lg border-2 text-center transition-all ${
                    formData.meal_plan === 'with_meals' 
                      ? 'border-amber-500 bg-amber-500/20' 
                      : 'border-gray-600 bg-gray-800 hover:border-gray-500'
                  } ${formData.num_guests < mealPricing.min_occupancy ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="font-semibold text-white">Room + 4 Meals</div>
                  <div className="text-xs text-gray-400 mt-1">Per person per day</div>
                </button>
              </div>
            </div>

            <div>
              <Label className="text-gray-300">Full Name *</Label>
              <Input
                value={formData.guest_name}
                onChange={(e) => setFormData({ ...formData, guest_name: e.target.value })}
                placeholder="Enter your full name"
                className="bg-gray-800 border-gray-600 text-white"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Email *</Label>
                <Input
                  type="email"
                  value={formData.guest_email}
                  onChange={(e) => setFormData({ ...formData, guest_email: e.target.value })}
                  placeholder="your@email.com"
                  className="bg-gray-800 border-gray-600 text-white"
                />
              </div>
              <div>
                <Label className="text-gray-300">Phone *</Label>
                <Input
                  value={formData.guest_phone}
                  onChange={(e) => setFormData({ ...formData, guest_phone: e.target.value })}
                  placeholder="+91 9876543210"
                  className="bg-gray-800 border-gray-600 text-white"
                />
              </div>
            </div>

            <div>
              <Label className="text-gray-300">Special Requests</Label>
              <Textarea
                value={formData.special_requests}
                onChange={(e) => setFormData({ ...formData, special_requests: e.target.value })}
                placeholder="Any special requirements..."
                className="bg-gray-800 border-gray-600 text-white"
                rows={3}
              />
            </div>

            {/* Price Summary */}
            {calculating && (
              <div className="bg-gray-800 p-4 rounded-lg text-center">
                <p className="text-gray-400">Calculating price...</p>
              </div>
            )}
            
            {priceData && !calculating && (
              <div className="bg-gray-800 p-4 rounded-lg">
                <h4 className="font-semibold text-white mb-3">Price Summary</h4>
                {formData.meal_plan === 'room_only' ? (
                  <div className="space-y-2">
                    <div className="flex justify-between text-gray-300">
                      <span>Room ({priceData.num_nights} night{priceData.num_nights > 1 ? 's' : ''} × ₹{selectedRoom?.base_price})</span>
                      <span>₹{priceData.total_amount}</span>
                    </div>
                    <div className="text-xs text-gray-500">
                      {formData.num_guests} guest{formData.num_guests > 1 ? 's' : ''} • Flat rate
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex justify-between text-gray-300">
                      <span>Room + 4 Meals</span>
                      <span>₹{priceData.total_amount}</span>
                    </div>
                    <div className="text-xs text-amber-400">
                      ₹{priceData.breakdown?.per_person_rate}/person × {formData.num_guests} guests × {priceData.num_nights} night{priceData.num_nights > 1 ? 's' : ''}
                    </div>
                    <div className="text-xs text-gray-500">
                      Includes: Breakfast, Lunch, Tea, Dinner
                    </div>
                  </div>
                )}
                <div className="flex justify-between border-t border-gray-700 pt-3 mt-3">
                  <span className="font-bold text-white">Total Amount</span>
                  <span className="font-bold text-white text-xl">₹{priceData.total_amount.toLocaleString()}</span>
                </div>
                
                {/* 50% Advance Payment Breakdown */}
                <div className="mt-4 bg-green-900/50 border border-green-600 rounded-lg p-3">
                  <h5 className="text-green-400 font-semibold mb-2 text-sm">💳 Payment Breakdown (50% Advance)</h5>
                  <div className="space-y-2">
                    <div className="flex justify-between text-green-300">
                      <span>Advance Payment (50%)</span>
                      <span className="font-bold text-green-400">₹{Math.round(priceData.total_amount * 0.5).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-orange-300">
                      <span>Balance at Check-in</span>
                      <span className="font-semibold">₹{Math.round(priceData.total_amount * 0.5).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!priceData && !calculating && formData.check_in && formData.check_out && (
              <div className="bg-gray-800 p-4 rounded-lg text-center">
                <p className="text-gray-400">Select booking type to see price</p>
              </div>
            )}

            <Button
              onClick={handleSubmitBooking}
              disabled={booking || !priceData}
              className="w-full bg-green-600 hover:bg-green-700 py-6 text-lg disabled:opacity-50"
              data-testid="proceed-to-payment-btn"
            >
              {booking ? 'Processing...' : 'Proceed to Payment'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Floating Quick Book Button */}
      <button
        onClick={() => {
          const bookingSection = document.getElementById('book-now-section');
          if (bookingSection) {
            bookingSection.scrollIntoView({ behavior: 'smooth' });
          }
        }}
        className="fixed bottom-6 right-6 z-50 bg-green-600 hover:bg-green-700 text-white px-6 py-4 rounded-full shadow-2xl flex items-center gap-2 transition-all hover:scale-105 animate-pulse hover:animate-none"
        data-testid="quick-book-btn"
      >
        <CalendarIcon size={20} />
        <span className="font-semibold">Quick Book</span>
      </button>
    </div>
  );
};

export default PublicBooking;
